import { SpotStreamRegistry } from './spot'
import { AccountStreamRegistry } from './account'
import { DerivativeStreamRegistry } from './derivative'

/**
 * Unified StreamRegistry that combines all stream types
 *
 * This registry provides a single interface for managing all application streams:
 * - Spot market streams (orderbook, trades, orders, etc.)
 * - Derivative market streams (orderbook, trades, orders, etc.)
 * - Account streams (positions, balances, grid strategies)
 *
 * All streams are managed with automatic retry, inactivity detection, and reconnection.
 */
class StreamRegistry {
  private derivativeRegistry = new DerivativeStreamRegistry()
  unsubscribeDerivativeSubaccountTrades =
    this.derivativeRegistry.unsubscribeDerivativeSubaccountTrades.bind(
      this.derivativeRegistry
    )
  subscribeDerivativeSubaccountTrades =
    this.derivativeRegistry.subscribeDerivativeSubaccountTrades.bind(
      this.derivativeRegistry
    )

  // ============================================================
  // SPOT STREAMS
  // ============================================================

  unsubscribeDerivativeOrderHistory =
    this.derivativeRegistry.unsubscribeDerivativeOrderHistory.bind(
      this.derivativeRegistry
    )
  subscribeDerivativeOrderHistory =
    this.derivativeRegistry.subscribeDerivativeOrderHistory.bind(
      this.derivativeRegistry
    )

  unsubscribeDerivativeOrderbook =
    this.derivativeRegistry.unsubscribeDerivativeOrderbook.bind(
      this.derivativeRegistry
    )
  subscribeDerivativeOrderbook =
    this.derivativeRegistry.subscribeDerivativeOrderbook.bind(
      this.derivativeRegistry
    )

  unsubscribeDerivativeTrades =
    this.derivativeRegistry.unsubscribeDerivativeTrades.bind(
      this.derivativeRegistry
    )
  unsubscribeDerivativeOrders =
    this.derivativeRegistry.unsubscribeDerivativeOrders.bind(
      this.derivativeRegistry
    )

  subscribeDerivativeTrades =
    this.derivativeRegistry.subscribeDerivativeTrades.bind(
      this.derivativeRegistry
    )
  subscribeDerivativeOrders =
    this.derivativeRegistry.subscribeDerivativeOrders.bind(
      this.derivativeRegistry
    )

  private spotRegistry = new SpotStreamRegistry()
  unsubscribeSpotSubaccountTrades =
    this.spotRegistry.unsubscribeSpotSubaccountTrades.bind(this.spotRegistry)

  private accountRegistry = new AccountStreamRegistry()
  unsubscribeSubaccountBalance =
    this.accountRegistry.unsubscribeSubaccountBalance.bind(this.accountRegistry)

  // ============================================================
  // DERIVATIVE STREAMS
  // ============================================================

  subscribeSpotSubaccountTrades =
    this.spotRegistry.subscribeSpotSubaccountTrades.bind(this.spotRegistry)
  unsubscribeSpotAverageEntries =
    this.spotRegistry.unsubscribeSpotAverageEntries.bind(this.spotRegistry)

  unsubscribeMarkPrices = this.derivativeRegistry.unsubscribeMarkPrices.bind(
    this.derivativeRegistry
  )
  subscribeSubaccountBalance =
    this.accountRegistry.subscribeSubaccountBalance.bind(this.accountRegistry)

  subscribeGridStrategies = this.accountRegistry.subscribeGridStrategies.bind(
    this.accountRegistry
  )
  unsubscribeGridStrategies =
    this.accountRegistry.unsubscribeGridStrategies.bind(this.accountRegistry)

  subscribeSpotOrderHistory = this.spotRegistry.subscribeSpotOrderHistory.bind(
    this.spotRegistry
  )
  unsubscribeSpotOrderHistory =
    this.spotRegistry.unsubscribeSpotOrderHistory.bind(this.spotRegistry)

  subscribeSpotAverageEntries =
    this.spotRegistry.subscribeSpotAverageEntries.bind(this.spotRegistry)
  subscribeMarkPrices = this.derivativeRegistry.subscribeMarkPrices.bind(
    this.derivativeRegistry
  )

  unsubscribeBankBalance = this.accountRegistry.unsubscribeBankBalance.bind(
    this.accountRegistry
  )
  unsubscribeSpotOrderbook = this.spotRegistry.unsubscribeSpotOrderbook.bind(
    this.spotRegistry
  )

  // ============================================================
  // ACCOUNT STREAMS
  // ============================================================

  unsubscribePositions = this.accountRegistry.unsubscribePositions.bind(
    this.accountRegistry
  )
  subscribeBankBalance = this.accountRegistry.subscribeBankBalance.bind(
    this.accountRegistry
  )

  subscribeSpotOrderbook = this.spotRegistry.subscribeSpotOrderbook.bind(
    this.spotRegistry
  )
  unsubscribeSpotTrades = this.spotRegistry.unsubscribeSpotTrades.bind(
    this.spotRegistry
  )

  unsubscribeSpotOrders = this.spotRegistry.unsubscribeSpotOrders.bind(
    this.spotRegistry
  )
  subscribePositions = this.accountRegistry.subscribePositions.bind(
    this.accountRegistry
  )

  subscribeSpotTrades = this.spotRegistry.subscribeSpotTrades.bind(
    this.spotRegistry
  )
  subscribeSpotOrders = this.spotRegistry.subscribeSpotOrders.bind(
    this.spotRegistry
  )

  getActiveStreams(): string[] {
    return [
      ...this.spotRegistry.getActiveStreams(),
      ...this.derivativeRegistry.getActiveStreams(),
      ...this.accountRegistry.getActiveStreams()
    ]
  }

  unsubscribeAll(): void {
    this.spotRegistry.unsubscribeAll()
    this.derivativeRegistry.unsubscribeAll()
    this.accountRegistry.unsubscribeAll()
  }
}

export const streamRegistry = new StreamRegistry()
